import { useState, useEffect, useMemo } from 'react'
import { Plus, Search, Edit2, X } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import {
  standardNameService,
  type StandardName,
  type StandardNameGroup,
  type StandardNamesStats,
} from '@/services/standardNames'
import { useCan } from '@/features/auth/AuthContext'

const GROUPS: Array<{ key: string; label: string }> = [
  { key: '', label: 'Tất cả nhóm' },
  { key: 'ngũ kim', label: 'Ngũ kim' },
  { key: 'bao bì', label: 'Bao bì' },
  { key: 'gỗ', label: 'Gỗ' },
  { key: 'ván', label: 'Ván' },
  { key: 'khác', label: 'Khác' },
]

export function StandardNamesPage() {
  const canWriteBom = useCan('bom.write')
  const canWriteQuotes = useCan('quotes.write')
  const canWrite = canWriteBom || canWriteQuotes
  const [items, setItems] = useState<StandardName[]>([])
  const [stats, setStats] = useState<StandardNamesStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedGroup, setSelectedGroup] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // Modals state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<StandardName | null>(null)
  const [formGroup, setFormGroup] = useState<StandardNameGroup>('ngũ kim')
  const [formName, setFormName] = useState('')
  const [formUom, setFormUom] = useState('')
  const [formSpec, setFormSpec] = useState('')
  const [formActive, setFormActive] = useState(true)

  const [isAliasModalOpen, setIsAliasModalOpen] = useState(false)
  const [aliasParentItem, setAliasParentItem] = useState<StandardName | null>(null)
  const [newAlias, setNewAlias] = useState('')
  const [newAliasNote, setNewAliasNote] = useState('')
  const [saving, setSaving] = useState(false)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const res = await standardNameService.list()
      setItems(res.items)
      setStats(res.stats)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được từ điển tên chuẩn')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (selectedGroup && item.group !== selectedGroup) return false
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const matchesName = item.standard_name.toLowerCase().includes(term)
        const matchesAlias = item.aliases.some((a) => a.alias.toLowerCase().includes(term))
        if (!matchesName && !matchesAlias) return false
      }
      return true
    })
  }, [items, selectedGroup, searchTerm])

  const totalBomUsage = useMemo(
    () => items.reduce((sum, item) => sum + (item.bom_usage_count || 0), 0),
    [items],
  )

  function openCreateModal() {
    setEditingItem(null)
    setFormGroup('ngũ kim')
    setFormName('')
    setFormUom('Pcs')
    setFormSpec('')
    setFormActive(true)
    setIsEditModalOpen(true)
  }

  function openEditModal(item: StandardName) {
    setEditingItem(item)
    setFormGroup(item.group)
    setFormName(item.standard_name)
    setFormUom(item.default_uom || '')
    setFormSpec(item.spec_hint || '')
    setFormActive(item.active)
    setIsEditModalOpen(true)
  }

  function openAddAliasModal(item: StandardName) {
    setAliasParentItem(item)
    setNewAlias('')
    setNewAliasNote('')
    setIsAliasModalOpen(true)
  }

  async function handleSaveItem() {
    if (!formName.trim()) return
    setSaving(true)
    try {
      if (editingItem) {
        await standardNameService.update(editingItem.id, {
          group: formGroup,
          standard_name: formName.trim(),
          default_uom: formUom.trim() || null,
          spec_hint: formSpec.trim() || null,
          active: formActive,
        })
      } else {
        await standardNameService.create({
          group: formGroup,
          standard_name: formName.trim(),
          default_uom: formUom.trim() || undefined,
          spec_hint: formSpec.trim() || undefined,
        })
      }
      setIsEditModalOpen(false)
      await reload()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi lưu tên chuẩn')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveAlias() {
    if (!aliasParentItem || !newAlias.trim()) return
    setSaving(true)
    try {
      await standardNameService.addAlias(
        aliasParentItem.id,
        newAlias.trim(),
        newAliasNote.trim() || undefined,
      )
      setIsAliasModalOpen(false)
      await reload()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi thêm bí danh')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteAlias(aliasId: string) {
    if (!confirm('Bạn có chắc chắn muốn xóa bí danh này?')) return
    try {
      await standardNameService.deleteAlias(aliasId)
      await reload()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi xóa bí danh')
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <Breadcrumb
            items={[
              { label: 'Trang chủ', to: '/' },
              { label: 'Mua hàng', to: '/purchasing' },
              { label: 'Từ điển tên chuẩn', to: '/purchasing/standard-names' },
            ]}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <PageHeader
              title="Từ điển tên chuẩn vật tư"
              subtitle="Quy chuẩn tên gọi thống nhất cho BOM và Báo giá NCC (ngũ kim, bao bì, gỗ, ván)"
            />
            {canWrite && (
              <Button variant="primary" onClick={openCreateModal}>
                <Plus className="mr-1.5 h-4 w-4" />
                Thêm tên chuẩn
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {/* KPI Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KpiCard
            label="Tổng số tên chuẩn"
            value={stats?.totalNames ?? items.length}
            tone="default"
          />
          <KpiCard
            label="Tổng số bí danh"
            value={stats?.totalAliases ?? 0}
            tone="default"
          />
          <KpiCard
            label="Đang áp dụng"
            value={stats?.activeNames ?? items.filter((x) => x.active).length}
            tone="success"
          />
          <KpiCard
            label="Số dòng BOM khớp"
            value={totalBomUsage}
            tone="warning"
          />
        </div>

        {/* Toolbar / Filters */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            {GROUPS.map((g) => (
              <button
                key={g.key}
                onClick={() => setSelectedGroup(g.key)}
                className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  selectedGroup === g.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div className="relative min-w-[240px]">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm theo tên hoặc bí danh..."
              className="h-8 w-full rounded border border-slate-200 pl-8 pr-3 text-xs outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Data Table */}
        {loading ? (
          <PageLoading label="Đang tải từ điển tên chuẩn..." />
        ) : error ? (
          <PageError message={error} onRetry={reload} />
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                <thead className="bg-slate-50 font-semibold text-slate-600">
                  <tr>
                    <th className="w-12 px-3 py-2.5 text-center">STT</th>
                    <th className="px-3 py-2.5">Tên chuẩn</th>
                    <th className="px-3 py-2.5">Nhóm</th>
                    <th className="px-3 py-2.5">ĐVT</th>
                    <th className="px-3 py-2.5">Gợi ý quy cách</th>
                    <th className="px-3 py-2.5">Bí danh ghi nhận</th>
                    <th className="px-3 py-2.5 text-right">Dòng BOM</th>
                    <th className="px-3 py-2.5 text-right">Báo giá</th>
                    <th className="px-3 py-2.5 text-center">Trạng thái</th>
                    {canWrite && <th className="px-3 py-2.5 text-center">Thao tác</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-slate-400">
                        Không tìm thấy tên chuẩn nào phù hợp
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item, idx) => (
                      <tr key={item.id} className="hover:bg-slate-50/80">
                        <td className="px-3 py-2.5 text-center font-medium text-slate-400 tabular-nums">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-slate-900">
                          {item.standard_name}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 capitalize">
                            {item.group}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{item.default_uom || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-500 max-w-xs truncate" title={item.spec_hint || ''}>
                          {item.spec_hint || '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {item.aliases.map((a) => (
                              <span
                                key={a.id}
                                className="group inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 border border-blue-200/60"
                                title={a.note ? `Ghi chú: ${a.note}` : undefined}
                              >
                                {a.alias}
                                {canWrite && (
                                  <button
                                    onClick={() => handleDeleteAlias(a.id)}
                                    className="ml-1 hidden text-blue-400 hover:text-red-600 group-hover:inline-block"
                                    title="Xóa bí danh"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </span>
                            ))}
                            {canWrite && (
                              <button
                                onClick={() => openAddAliasModal(item)}
                                className="rounded border border-dashed border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-500 hover:border-blue-400 hover:text-blue-600"
                                title="Thêm bí danh mới"
                              >
                                + Thêm
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700 tabular-nums">
                          {item.bom_usage_count || 0}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700 tabular-nums">
                          {item.quote_usage_count || 0}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {item.active ? (
                            <StatusBadge tone="success" label="Đang dùng" />
                          ) : (
                            <StatusBadge tone="neutral" label="Ngưng" />
                          )}
                        </td>
                        {canWrite && (
                          <td className="px-3 py-2.5 text-center">
                            <button
                              onClick={() => openEditModal(item)}
                              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-blue-600"
                              title="Chỉnh sửa tên chuẩn"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal Thêm / Sửa Tên Chuẩn */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-4 text-base font-semibold text-slate-900">
              {editingItem ? 'Chỉnh sửa tên chuẩn' : 'Thêm tên chuẩn mới'}
            </h3>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block mb-1 font-medium text-slate-700">Nhóm vật tư *</label>
                <select
                  value={formGroup}
                  onChange={(e) => setFormGroup(e.target.value as StandardNameGroup)}
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                >
                  <option value="ngũ kim">Ngũ kim</option>
                  <option value="bao bì">Bao bì</option>
                  <option value="gỗ">Gỗ</option>
                  <option value="ván">Ván</option>
                  <option value="khác">Khác</option>
                </select>
              </div>

              <div>
                <label className="block mb-1 font-medium text-slate-700">Tên chuẩn *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ví dụ: Bu lông tay nắm"
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 font-medium text-slate-700">Đơn vị tính mặc định</label>
                  <input
                    type="text"
                    value={formUom}
                    onChange={(e) => setFormUom(e.target.value)}
                    placeholder="Pcs, cái, m³, bộ..."
                    className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-1 font-medium text-slate-700">Trạng thái</label>
                  <select
                    value={formActive ? '1' : '0'}
                    onChange={(e) => setFormActive(e.target.value === '1')}
                    className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                  >
                    <option value="1">Đang dùng</option>
                    <option value="0">Ngưng dùng</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block mb-1 font-medium text-slate-700">Gợi ý quy cách / mô tả</label>
                <textarea
                  value={formSpec}
                  onChange={(e) => setFormSpec(e.target.value)}
                  rows={2}
                  placeholder="Ví dụ: 80x80x80 hoặc 100x100x100..."
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                Hủy
              </Button>
              <Button variant="primary" disabled={saving || !formName.trim()} onClick={handleSaveItem}>
                {saving ? 'Đang lưu...' : 'Lưu tên chuẩn'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Thêm Bí Danh */}
      {isAliasModalOpen && aliasParentItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-2 text-base font-semibold text-slate-900">Thêm bí danh mới</h3>
            <p className="mb-4 text-xs text-slate-500">
              Tên chuẩn: <strong className="text-slate-800">{aliasParentItem.standard_name}</strong>
            </p>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block mb-1 font-medium text-slate-700">Bí danh (tên viết khác) *</label>
                <input
                  type="text"
                  value={newAlias}
                  onChange={(e) => setNewAlias(e.target.value)}
                  placeholder="Ví dụ: Bulong, Vis, Guzong..."
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                  autoFocus
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-slate-700">Ghi chú (nếu có)</label>
                <input
                  type="text"
                  value={newAliasNote}
                  onChange={(e) => setNewAliasNote(e.target.value)}
                  placeholder="Ví dụ: Ghi nhận trong BOM cũ, lỗi chính tả..."
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setIsAliasModalOpen(false)}>
                Hủy
              </Button>
              <Button variant="primary" disabled={saving || !newAlias.trim()} onClick={handleSaveAlias}>
                {saving ? 'Đang lưu...' : 'Thêm bí danh'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
